import React, { useState } from 'react';
import { Camera, MapPin, Heart, MessageCircle, Share2, MoreHorizontal, Plus, X, Image as ImageIcon, Flame, Star, Coffee, Music } from 'lucide-react';
import { useUser } from "@clerk/react";

const MomentsView = () => {
    // Safely get user or fallback to Guest if Clerk is not initialized
    const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
    const isValidKey = PUBLISHABLE_KEY && !PUBLISHABLE_KEY.includes("eznaija.com");
    
    let clerkUser = null;
    try {
        if (isValidKey) {
            const res = useUser();
            clerkUser = res.user;
        }
    } catch (e) {
        console.warn("Clerk context not available in MomentsView");
    }

    const [activeTab, setActiveTab] = useState('Trending');
    const [moments, setMoments] = useState([
        {
            id: 1,
            user: 'LagosExplorer',
            location: 'Lekki Conservation Centre',
            area: 'Lekki',
            image: 'https://images.unsplash.com/photo-1590059536241-11910609335a?auto=format&fit=crop&q=80&w=800',
            likes: 124,
            caption: 'The canopy walk is a must-do! 🌿 #LagosLife #EZTravel',
            category: 'Attractions',
            liked: false
        },
        {
            id: 2,
            user: 'Foodie_NI',
            location: 'Yellow Chilli',
            area: 'VI',
            image: 'https://images.unsplash.com/photo-1541014741259-df5290ce505a?auto=format&fit=crop&q=80&w=800',
            likes: 89,
            caption: 'Authentic flavors that hit different. 🍚🔥',
            category: 'Food',
            liked: false
        }
    ]);

    const feedCategories = [
        { id: 'Trending', icon: <Flame size={14} />, label: 'Trending' },
        { id: 'Food', icon: <Coffee size={14} />, label: 'Food' },
        { id: 'Attractions', icon: <Star size={14} />, label: 'Places' },
        { id: 'Nightlife', icon: <Music size={14} />, label: 'Nightlife' }
    ];

    const [showCreate, setShowCreate] = useState(false);
    const [newMoment, setNewMoment] = useState({
        location: '',
        area: 'VI',
        caption: '',
        category: 'Food',
    });

    const handleLike = (id) => {
        setMoments(moments.map(m => {
            if (m.id === id) {
                return { ...m, likes: m.liked ? m.likes - 1 : m.likes + 1, liked: !m.liked };
            }
            return m;
        }));
    };

    const handlePost = (e) => {
        e.preventDefault();
        const moment = {
            id: Date.now(),
            user: clerkUser?.firstName || clerkUser?.username || 'Guest',
            location: newMoment.location,
            area: newMoment.area,
            image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&q=80&w=800',
            likes: 0,
            caption: newMoment.caption,
            category: newMoment.category,
            liked: false
        };
        setMoments([moment, ...moments]);
        setShowCreate(false);
    };

    return (
        <div className="moments-view animate-fade">
            <div className="moments-header">
                <div className="header-title">
                    <h2>Lagos Moments</h2>
                    <p>Shared experiences from the community</p>
                </div>
                <button className="create-post-btn" onClick={() => setShowCreate(true)}>
                    <Plus size={20} />
                </button>
            </div>

            <div className="feed-cats">
                {feedCategories.map(cat => (
                    <button 
                        key={cat.id} 
                        className={`feed-cat-pill ${activeTab === cat.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(cat.id)}
                    >
                        {cat.icon}
                        <span>{cat.label}</span>
                    </button>
                ))}
            </div>

            <div className="moments-feed">
                {moments.filter(m => activeTab === 'Trending' || m.category === activeTab).map(moment => (
                    <div key={moment.id} className="moment-card-premium">
                        <div className="moment-meta-row">
                            <div className="user-info-box">
                                <div className="user-avatar">{moment.user.charAt(0)}</div>
                                <div className="user-details">
                                    <h4>{moment.user}</h4>
                                    <span className="location-row">
                                        <MapPin size={10} /> {moment.location} • {moment.area}
                                    </span>
                                </div>
                            </div>
                            <button className="more-btn"><MoreHorizontal size={18} /></button>
                        </div>
                        
                        <div className="moment-img-container" onDoubleClick={() => handleLike(moment.id)}>
                            <img src={moment.image} alt="Moment Content" />
                            <div className="cat-badge">{moment.category}</div>
                        </div>

                        <div className="moment-actions-footer">
                            <div className="interaction-row">
                                <div className="main-btns">
                                    <button onClick={() => handleLike(moment.id)} className={moment.liked ? 'liked' : ''}>
                                        <Heart size={24} fill={moment.liked ? "var(--emergency-red)" : "none"} color={moment.liked ? "var(--emergency-red)" : "#222"} />
                                    </button>
                                    <button><MessageCircle size={24} color="#222" /></button>
                                    <button><Share2 size={24} color="#222" /></button>
                                </div>
                                <div className="engagement-info">
                                    <b>{moment.likes.toLocaleString()} likes</b>
                                </div>
                            </div>
                            <p className="caption-text">
                                <b>{moment.user}</b> {moment.caption}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {showCreate && (
                <div className="create-overlay">
                    <div className="create-modal animate-fade">
                        <div className="modal-top">
                            <h3>Share a Moment</h3>
                            <button onClick={() => setShowCreate(false)}><X size={24} /></button>
                        </div>
                        <form onSubmit={handlePost}>
                            <div className="m-input-group">
                                <label>Where are you?</label>
                                <input type="text" placeholder="e.g., Nike Art Gallery" required value={newMoment.location} onChange={e => setNewMoment({...newMoment, location: e.target.value})} />
                            </div>
                            <div className="m-input-row">
                                <div className="m-input-group">
                                    <label>Area</label>
                                    <select value={newMoment.area} onChange={e => setNewMoment({...newMoment, area: e.target.value})}>
                                        <option>Lekki</option><option>VI</option><option>Ikoyi</option><option>Ikeja</option>
                                    </select>
                                </div>
                                <div className="m-input-group">
                                    <label>Type</label>
                                    <select value={newMoment.category} onChange={e => setNewMoment({...newMoment, category: e.target.value})}>
                                        <option>Food</option><option>Attractions</option><option>Nightlife</option><option>Culture</option>
                                    </select>
                                </div>
                            </div>
                            <div className="m-input-group">
                                <label>The Vibe</label>
                                <textarea rows="3" placeholder="Tell us more about it..." required value={newMoment.caption} onChange={e => setNewMoment({...newMoment, caption: e.target.value})}></textarea>
                            </div>
                            <div className="fake-upload-zone" onClick={() => alert("Ready to upload")}>
                                <ImageIcon size={28} />
                                <span>Add Photos</span>
                            </div>
                            <button type="submit" className="post-submit-btn">Post to EZ Naija</button>
                        </form>
                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
        .moments-view { padding-bottom: 90px; }
        .moments-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding: 20px 20px 0; }
        .header-title h2 { font-size: 28px; fontWeight: 800; color: var(--text-dark); margin: 0; }
        .header-title p { font-size: 14px; color: var(--text-light); margin-top: 4px; }
        .create-post-btn { width: 48px; height: 48px; border-radius: 50%; background: var(--primary-green); color: white; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 16px rgba(0, 104, 56, 0.2); transition: 0.3s; }
        .create-post-btn:hover { transform: scale(1.05); }

        .feed-cats { display: flex; gap: 10px; overflow-x: auto; padding: 0 20px 20px; scrollbar-width: none; }
        .feed-cats::-webkit-scrollbar { display: none; }
        .feed-cat-pill { display: flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 20px; background: white; border: 1px solid var(--border-gray); white-space: nowrap; font-size: 13px; font-weight: 700; color: var(--text-light); box-shadow: 0 4px 10px rgba(0,0,0,0.03); }
        .feed-cat-pill.active { background: var(--primary-green); color: white; border-color: var(--primary-green); }

        .moments-feed { display: flex; flex-direction: column; gap: 30px; padding: 0 20px; }
        .moment-card-premium { background: white; border-radius: 30px; border: 1px solid var(--border-gray); overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.05); }
        
        .moment-meta-row { display: flex; justify-content: space-between; align-items: center; padding: 16px; }
        .user-info-box { display: flex; align-items: center; gap: 12px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 12px; background: var(--light-green); color: var(--primary-green); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; }
        .user-details h4 { font-size: 15px; margin: 0; color: var(--text-dark); }
        .location-row { font-size: 11px; color: var(--text-light); display: flex; align-items: center; gap: 4px; }
        .more-btn { color: #ccc; }

        .moment-img-container { position: relative; width: 100%; aspect-ratio: 4/5; }
        .moment-img-container img { width: 100%; height: 100%; object-fit: cover; }
        .cat-badge { position: absolute; top: 16px; right: 16px; background: rgba(255,255,255,0.9); backdrop-filter: blur(4px); padding: 4px 12px; border-radius: 12px; font-size: 10px; font-weight: 800; color: var(--primary-green); text-transform: uppercase; }

        .moment-actions-footer { padding: 16px; }
        .interaction-row { display: flex; flex-direction: column; gap: 12px; margin-bottom: 12px; }
        .main-btns { display: flex; gap: 18px; }
        .main-btns button { padding: 0; background: none; transition: 0.2s; }
        .main-btns button:active { transform: scale(1.2); }
        .engagement-info { font-size: 14px; color: var(--text-dark); }
        .caption-text { font-size: 14px; line-height: 1.6; color: var(--text-dark); }
        .caption-text b { margin-right: 6px; }

        .create-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); backdrop-filter: blur(5px); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .create-modal { background: white; width: 100%; max-width: 440px; border-radius: 40px; padding: 32px; box-shadow: 0 20px 50px rgba(0,0,0,0.2); }
        .modal-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .modal-top h3 { font-size: 20px; fontWeight: 800; margin: 0; }
        .m-input-group { margin-bottom: 20px; }
        .m-input-group label { display: block; font-size: 12px; font-weight: 800; color: var(--text-light); text-transform: uppercase; margin-bottom: 8px; }
        .m-input-group input, .m-input-group select, .m-input-group textarea { width: 100%; padding: 14px; border: 1.5px solid var(--border-gray); border-radius: 16px; font-family: inherit; font-size: 15px; outline: none; transition: 0.2s; }
        .m-input-group input:focus { border-color: var(--primary-green); }
        .m-input-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        
        .fake-upload-zone { height: 100px; border: 2px dashed var(--border-gray); border-radius: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: var(--text-light); margin-bottom: 24px; cursor: pointer; }
        .fake-upload-zone span { font-size: 13px; font-weight: 700; }
        .post-submit-btn { width: 100%; padding: 16px; background: var(--primary-green); color: white; border-radius: 20px; font-weight: 800; font-size: 16px; box-shadow: 0 10px 20px rgba(0, 104, 56, 0.2); }
      `}} />
        </div>
    );
};

export default MomentsView;
